import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Search,
  FileText,
  Briefcase,
  Play,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Award,
  Download,
  RefreshCw,
  Eye,
  Filter,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  Target,
  Calendar
} from 'lucide-react';
import { toast } from 'react-toastify';

import { offersAPI, cvsAPI, analysisAPI, resultsAPI } from '../../utils/api';
import { getErrorMessage } from '../../utils/errorTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';
import SkillsRadarChart from '../../components/charts/SkillsRadarChart';

const MAX_CVS = 25;

const AnalyzerPage = () => {
  const { confirm, confirmProps } = useConfirm();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  const [mode, setMode] = useState('manual');
  const [offers, setOffers] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [selectedCvs, setSelectedCvs] = useState([]);
  const [analyzedCvIds, setAnalyzedCvIds] = useState(new Set());
  const [analysisStatus, setAnalysisStatus] = useState({ analyzed: 0, unanalyzed: 0, total: 0 });

  const [loading, setLoading] = useState(true);
  const [cvsLoading, setCvsLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [analysisFilter, setAnalysisFilter] = useState('all');

  const [analysisJob, setAnalysisJob] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const normalizeRecommendationKey = useCallback((recommendation) => {
    const value = (recommendation || '').toString().trim().toLowerCase();
    if (['recommandé', 'recommended', 'recomendado'].includes(value)) return 'recommended';
    if (['à considérer', 'to consider', 'consider', 'a considerar'].includes(value)) return 'consider';
    if (['non recommandé', 'not recommended', 'no recomendado'].includes(value)) return 'not_recommended';
    return null;
  }, []);

  const formatResultDate = useCallback((dateValue) => {
    if (!dateValue) return '-';
    const locale =
      currentLanguage === 'fr'
        ? 'fr-FR'
        : currentLanguage === 'es'
          ? 'es-ES'
          : 'en-US';
    return new Date(dateValue).toLocaleDateString(locale);
  }, [currentLanguage]);

  const selectedOffer = useMemo(() => {
    if (!selectedOfferId) return null;
    return offers.find((offer) => offer.id.toString() === selectedOfferId) || null;
  }, [offers, selectedOfferId]);

  const filteredCvs = useMemo(() => {
    let list = cvs;

    if (analysisFilter === 'analyzed') {
      list = list.filter((cv) => analyzedCvIds.has(cv.id));
    } else if (analysisFilter === 'unanalyzed') {
      list = list.filter((cv) => !analyzedCvIds.has(cv.id));
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((cv) => (cv.original_name || '').toLowerCase().includes(term));
    }

    return list;
  }, [cvs, analysisFilter, analyzedCvIds, searchTerm]);

  const analysisStats = useMemo(() => {
    if (!analysisResults.length) {
      return {
        total: 0,
        recommended: 0,
        consider: 0,
        avgScore: 0
      };
    }

    const recommended = analysisResults.filter((r) => r.recommendation_key === 'recommended').length;
    const consider = analysisResults.filter((r) => r.recommendation_key === 'consider').length;
    const avgScore = analysisResults.reduce((sum, r) => sum + r.score, 0) / analysisResults.length;

    return {
      total: analysisResults.length,
      recommended,
      consider,
      avgScore: Math.round(avgScore)
    };
  }, [analysisResults]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setSelectedOfferId('');
      setSelectedCvs([]);
      setAnalysisResults([]);
      setAnalyzedCvIds(new Set());
      setAnalysisStatus({ analyzed: 0, unanalyzed: 0, total: 0 });
      setAnalysisJob(null);

      if (mode === 'manual') {
        const [offersResponse, cvsResponse] = await Promise.all([
          offersAPI.getAll({ status: 'open', is_published: false }),
          cvsAPI.getAll({ origin: 'manual', limit: 100 })
        ]);
        setOffers(offersResponse.data.offers || []);
        setCvs(cvsResponse.data.cvs || []);
      } else {
        const offersResponse = await offersAPI.getAll({ is_published: true });
        setOffers(offersResponse.data.offers || []);
        setCvs([]);
      }
    } catch (error) {
      toast.error(t('analyzer.errors.dataLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [mode, t]);

  const fetchCvsForOffer = useCallback(async (offerId) => {
    try {
      setCvsLoading(true);
      const response = await cvsAPI.getAll({ origin: 'application', offer_id: offerId, limit: 100 });
      setCvs(response.data.cvs || []);
    } catch (error) {
      toast.error(t('analyzer.errors.cvsLoadFailed'));
    } finally {
      setCvsLoading(false);
    }
  }, [t]);

  const fetchAnalysisStatus = useCallback(async (offerId) => {
    try {
      const response = await analysisAPI.getUnanalyzed(offerId);
      const data = response.data || {};
      setAnalysisStatus({
        analyzed: data.analyzed_count || 0,
        unanalyzed: data.unanalyzed_count || 0,
        total: data.total_cvs || 0
      });
      setAnalyzedCvIds(new Set(data.analyzed_cv_ids || []));
    } catch (error) {
      // Optional, do not block UX
    }
  }, []);

  const fetchResults = useCallback(async (offerId) => {
    if (!offerId) return;
    try {
      setResultsLoading(true);
      const response = await resultsAPI.getByOffer(offerId, { limit: 50 });
      const results = response.data.results || [];
      const ranked = results.map((result, index) => ({
        ...result,
        rank: result.rank || index + 1,
        recommendation_key: result.recommendation_key || normalizeRecommendationKey(result.recommendation)
      }));
      setAnalysisResults(ranked);
    } catch (error) {
      toast.error(t('analyzer.errors.analysisError'));
    } finally {
      setResultsLoading(false);
    }
  }, [normalizeRecommendationKey, t]);

  const getRecommendationColor = useCallback((recommendationKey) => {
    switch (recommendationKey) {
      case 'recommended':
        return 'text-green-600 bg-green-100';
      case 'consider':
        return 'text-yellow-600 bg-yellow-100';
      case 'not_recommended':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }, []);

  const getRecommendationLabel = useCallback((recommendationKey, fallbackLabel) => {
    switch (recommendationKey) {
      case 'recommended':
        return t('results.filters.recommended');
      case 'consider':
        return t('results.filters.consider');
      case 'not_recommended':
        return t('results.filters.notRecommended');
      default:
        return fallbackLabel || t('results.filters.allRecommendations');
    }
  }, [t]);

  const getScoreTextClass = useCallback((score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  }, []);

  const handleViewResult = useCallback(async (result) => {
    setSelectedResult(result);
    setShowDetailModal(true);
    setDetailLoading(true);

    try {
      const response = await resultsAPI.getDetail(result.id);
      const detail = response.data || {};
      setSelectedResult({
        ...result,
        candidate_name: detail.cv?.candidate_name ?? result.candidate_name,
        cv_name: detail.cv?.name ?? result.cv_name,
        cv_filename: detail.cv?.filename ?? result.cv_filename,
        offer_title: detail.offer?.title ?? result.offer_title ?? selectedOffer?.title,
        offer_client: detail.offer?.client ?? result.offer_client,
        score: detail.analysis?.score ?? result.score,
        analysis: detail.analysis?.details ?? result.analysis,
        strengths: detail.analysis?.strengths ?? result.strengths,
        weaknesses: detail.analysis?.weaknesses ?? result.weaknesses,
        recommendation: detail.analysis?.recommendation ?? result.recommendation,
        recommendation_key:
          detail.analysis?.recommendation_key ||
          result.recommendation_key ||
          normalizeRecommendationKey(detail.analysis?.recommendation ?? result.recommendation),
        skills_radar: detail.analysis?.skills_radar ?? result.skills_radar,
        created_at: detail.created_at ?? result.created_at
      });
    } catch (error) {
      toast.error(getErrorMessage(error, t, t('results.errors.resultsLoadFailed')));
    } finally {
      setDetailLoading(false);
    }
  }, [normalizeRecommendationKey, selectedOffer?.title, t]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData, mode]);

  useEffect(() => {
    if (!selectedOfferId) {
      setAnalyzedCvIds(new Set());
      setAnalysisStatus({ analyzed: 0, unanalyzed: 0, total: 0 });
      setAnalysisResults([]);
      setAnalysisJob(null);
      setSelectedCvs([]);
      return;
    }

    setAnalysisResults([]);
    setAnalysisJob(null);
    setSelectedCvs([]);
    fetchAnalysisStatus(selectedOfferId);
    fetchResults(selectedOfferId);

    if (mode === 'submissions') {
      fetchCvsForOffer(selectedOfferId);
    }
  }, [fetchAnalysisStatus, fetchCvsForOffer, fetchResults, mode, selectedOfferId]);

  useEffect(() => {
    if (!analysisJob?.id) return;

    let isActive = true;
    const pollStatus = async () => {
      try {
        const response = await analysisAPI.getJobStatus(analysisJob.id);
        if (!isActive) return;

        const job = response.data;
        setAnalysisJob({
          id: job.id,
          status: job.status,
          progress: job.progress || 0,
          message: job.progress_message || ''
        });

        if (job.status === 'completed') {
          setAnalyzing(false);
          toast.success(t('analyzer.messages.analysisComplete', { count: selectedCvs.length }));
          await fetchResults(selectedOfferId);
          setAnalysisJob(null);
          return;
        }

        if (job.status === 'failed') {
          setAnalyzing(false);
          const errorMessage = job.error_message || t('analyzer.errors.analysisError');
          toast.error(errorMessage);
          setAnalysisJob(null);
          return;
        }
      } catch (error) {
        if (!isActive) return;
        setAnalyzing(false);
        toast.error(getErrorMessage(error, t, t('analyzer.errors.analysisError')));
        setAnalysisJob(null);
      }
    };

    pollStatus();
    const timer = setInterval(pollStatus, 4000);

    return () => {
      isActive = false;
      clearInterval(timer);
    };
  }, [analysisJob?.id, fetchResults, selectedCvs.length, selectedOfferId, t]);

  const toggleCvSelection = (cvId) => {
    setSelectedCvs((prev) => {
      if (prev.includes(cvId)) {
        return prev.filter((id) => id !== cvId);
      }
      if (prev.length >= MAX_CVS) {
        toast.warning(t('analyzer.warnings.maxCvsLimit'));
        return prev;
      }
      return [...prev, cvId];
    });
  };

  const handleSelectAll = () => {
    const selectable = filteredCvs.filter((cv) => cv.extracted_text).map((cv) => cv.id);
    if (selectable.length > MAX_CVS) {
      toast.warning(t('analyzer.warnings.maxCvsLimit'));
    }
    setSelectedCvs(selectable.slice(0, MAX_CVS));
  };

  const handleDeselectAll = () => {
    setSelectedCvs([]);
  };

  const handleStartAnalysis = async (forceReanalysis = false) => {
    if (!selectedOfferId || selectedCvs.length === 0) {
      toast.error(t('analyzer.errors.selectOfferAndCvs'));
      return;
    }

    setAnalyzing(true);

    try {
      const payload = {
        offer_id: Number(selectedOfferId),
        cv_ids: selectedCvs,
        force_reanalysis: forceReanalysis
      };

      const response = await analysisAPI.analyzeAsync(payload);
      const jobId = response?.data?.job_id;
      if (!jobId) {
        throw new Error(t('analyzer.errors.analysisError'));
      }

      setAnalysisJob({
        id: jobId,
        status: response.data.status || 'pending',
        progress: 0,
        message: t('analyzer.messages.analysisStarted', 'Analyse démarrée...')
      });
      toast.info(t('analyzer.messages.analysisStarted', 'Analyse IA démarrée'));
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.action_required === 'confirm_reanalysis') {
        const details = error.response.data.details || {};
        const confirmed = await confirm({
          title: t('analyzer.reanalysisModal.title'),
          message: details.confirmation_message || t('analyzer.reanalysisModal.partialOverlapMessage', {
            analyzed: details.already_analyzed || 0,
            total: details.total_cvs || 0
          }),
          confirmText: t('analyzer.reanalysisModal.confirm'),
          cancelText: t('analyzer.reanalysisModal.cancel')
        });

        if (confirmed) {
          return handleStartAnalysis(true);
        }
        setAnalyzing(false);
      } else if (error.response?.data?.code === 'AI_CONFIG_MISSING') {
        const confirmed = await confirm({
          title: t('aiConfig.modal.title'),
          message: t('aiConfig.modal.message'),
          type: 'info',
          confirmText: t('aiConfig.modal.goToSettings'),
          cancelText: t('aiConfig.modal.cancel')
        });
        if (confirmed) {
          navigate('/dashboard/parametres?tab=developer');
        }
        setAnalyzing(false);
      } else if (error.response?.data?.code === 'AI_INVALID_TOKEN') {
        const errorMessage = getErrorMessage(
          error,
          t,
          t('aiConfig.errors.invalidToken', 'Invalid AI API key')
        );
        toast.error(errorMessage);
        setAnalyzing(false);
      } else if (error.response?.data?.code === 'MAX_CVS_EXCEEDED') {
        toast.error(t('analyzer.errors.maxCvsAtOnce'));
        setAnalyzing(false);
      } else if (error.response?.data?.code === 'MISSING_TEXT') {
        toast.error(t('analyzer.cvLabels.textRequired'));
        setAnalyzing(false);
      } else {
        toast.error(getErrorMessage(error, t, t('analyzer.errors.analysisError')));
        setAnalyzing(false);
      }
    }
  };

  const handleExport = async () => {
    if (!selectedOfferId) return;
    try {
      const response = await resultsAPI.exportCsv(selectedOfferId);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analysis-results-${selectedOfferId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('analyzer.messages.exportSuccess'));
    } catch (error) {
      toast.error(t('analyzer.errors.exportError'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>{t('common.loading') || 'Loading...'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Brain className="w-6 h-6 text-indigo-600" />
          {t('analyzer.title')}
        </h1>
        <p className="text-sm text-gray-600">{t('analyzer.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">{t('analyzer.modes.title')}</h2>
              <span className="text-xs text-gray-500">{mode === 'manual' ? t('analyzer.modes.manual.title') : t('analyzer.modes.submissions.title')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode('manual')}
                className={`p-4 rounded-lg border text-left transition ${mode === 'manual' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-200'}`}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  {t('analyzer.modes.manual.title')}
                </div>
                <p className="text-xs text-gray-500 mt-2">{t('analyzer.modes.manual.description')}</p>
              </button>
              <button
                type="button"
                onClick={() => setMode('submissions')}
                className={`p-4 rounded-lg border text-left transition ${mode === 'submissions' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-200'}`}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Briefcase className="w-4 h-4 text-indigo-600" />
                  {t('analyzer.modes.submissions.title')}
                </div>
                <p className="text-xs text-gray-500 mt-2">{t('analyzer.modes.submissions.description')}</p>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-sm font-semibold text-gray-900">{t('analyzer.selection.selectOffer')}</label>
            <div className="mt-3">
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={selectedOfferId}
                onChange={(event) => setSelectedOfferId(event.target.value)}
              >
                <option value="">{t('analyzer.selection.noOfferAvailable')}</option>
                {offers.map((offer) => (
                  <option key={offer.id} value={offer.id}>
                    {offer.title}
                  </option>
                ))}
              </select>
            </div>
            {selectedOfferId && (
              <p className="mt-2 text-xs text-gray-500">
                {t('analyzer.selection.analysisStatus', {
                  analyzed: analysisStatus.analyzed,
                  unanalyzed: analysisStatus.unanalyzed
                })}
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-900">
                {selectedOffer
                  ? t('analyzer.selection.receivedCvs', { title: selectedOffer.title, count: selectedCvs.length })
                  : t('analyzer.selection.manualCvs', { count: selectedCvs.length })}
              </h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:border-indigo-200"
                >
                  {t('analyzer.selection.selectAll')}
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:border-indigo-200"
                >
                  {t('analyzer.selection.deselectAll')}
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('analyzer.selection.searchPlaceholder')}
                  className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
                />
              </div>
              <div className="relative">
                <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  value={analysisFilter}
                  onChange={(event) => setAnalysisFilter(event.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
                >
                  <option value="all">{t('analyzer.selection.filterAll')}</option>
                  <option value="unanalyzed">{t('analyzer.selection.filterUnanalyzed')}</option>
                  <option value="analyzed">{t('analyzer.selection.filterAnalyzed')}</option>
                </select>
              </div>
            </div>

            <div className="mt-4 border border-gray-100 rounded-lg divide-y divide-gray-100 max-h-[360px] overflow-y-auto">
              {cvsLoading ? (
                <div className="p-6 flex items-center justify-center text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('common.loading') || 'Loading...'}
                </div>
              ) : (
                filteredCvs.map((cv) => {
                  const isSelected = selectedCvs.includes(cv.id);
                  const isAnalyzed = analyzedCvIds.has(cv.id);
                  const hasText = Boolean(cv.extracted_text);

                  return (
                    <label
                      key={cv.id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${!hasText ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        disabled={!hasText}
                        checked={isSelected}
                        onChange={() => toggleCvSelection(cv.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{cv.original_name}</div>
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {(cv.mime_type || '').includes('pdf') ? 'PDF' : 'DOCX'}
                          </span>
                          {isAnalyzed && (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="w-3 h-3" />
                              {t('analyzer.cvLabels.alreadyAnalyzed')}
                            </span>
                          )}
                          {!hasText && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <AlertCircle className="w-3 h-3" />
                              {t('analyzer.cvLabels.textRequired')}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}

              {!cvsLoading && filteredCvs.length === 0 && (
                <div className="p-6 text-sm text-gray-500 text-center">
                  {mode === 'manual'
                    ? t('analyzer.selection.noCvsAvailable')
                    : t('analyzer.selection.noCvsReceived')}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">{t('analyzer.analysisInfo.limitTitle')}</h3>
            <p className="text-xs text-gray-500 mt-2">{t('analyzer.analysisInfo.limitDescription')}</p>
            <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-100">
              <div className="text-xs text-gray-500">{t('analyzer.analysisInfo.startTitle')}</div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                {selectedOffer
                  ? t('analyzer.analysisInfo.startDescription', {
                      count: selectedCvs.length,
                      title: selectedOffer.title
                    })
                  : t('analyzer.analysisInfo.startDescriptionEmpty')}
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleStartAnalysis(false)}
              disabled={!selectedOfferId || selectedCvs.length === 0 || analyzing}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {analyzing ? t('analyzer.actions.analyzing') : t('analyzer.actions.startAnalysisButton')}
            </button>

            {analysisJob && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{analysisJob.message || '...'}</span>
                  <span>{analysisJob.progress || 0}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full mt-2">
                  <div
                    className="h-2 bg-indigo-500 rounded-full transition-all"
                    style={{ width: `${analysisJob.progress || 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">{t('analyzer.stats.analyzed')}</h3>
              <button
                type="button"
                onClick={() => fetchResults(selectedOfferId)}
                className="text-xs text-gray-500 flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                {t('results.actions.refresh') || 'Refresh'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs text-gray-500">{t('analyzer.stats.analyzed')}</div>
                <div className="text-lg font-semibold text-gray-900">{analysisStats.total}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs text-gray-500">{t('analyzer.stats.avgScore')}</div>
                <div className="text-lg font-semibold text-gray-900">{analysisStats.avgScore}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs text-gray-500">{t('analyzer.stats.recommended')}</div>
                <div className="text-lg font-semibold text-emerald-600">{analysisStats.recommended}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-xs text-gray-500">{t('analyzer.stats.consider')}</div>
                <div className="text-lg font-semibold text-amber-600">{analysisStats.consider}</div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={handleExport}
                disabled={!analysisResults.length}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {t('analyzer.actions.export')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard/resultats')}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 text-white px-3 py-2 text-sm"
              >
                <Eye className="w-4 h-4" />
                {t('results.actions.viewAllAnalyses') || 'View results'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">{t('analyzer.results.title')}</h2>
          {resultsLoading && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t('common.loading') || 'Loading...'}
            </div>
          )}
        </div>

        {analysisResults.length === 0 && !resultsLoading && (
          <div className="text-sm text-gray-500 mt-4">{t('results.messages.noResults')}</div>
        )}

        {analysisResults.length > 0 && (
          <div className="mt-4 space-y-3">
            {analysisResults.map((result) => (
              <div key={result.id} className="p-4 border border-gray-100 rounded-lg flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{result.cv_name}</div>
                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                    <span className="flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {t('analyzer.results.rank', { rank: result.rank || '-' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {result.score}/100
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${getRecommendationColor(result.recommendation_key)}`}>
                    {getRecommendationLabel(result.recommendation_key, result.recommendation)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleViewResult(result)}
                    className="text-xs px-2 py-1 rounded-lg border border-gray-200"
                  >
                    {t('analyzer.results.viewDetails')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDetailModal && selectedResult && (
        <div className="fixed inset-0 bg-black/40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border border-gray-200 w-full max-w-5xl shadow-xl rounded-lg bg-white">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('results.detailModal.title').replace('{{filename}}', selectedResult.candidate_name || selectedResult.cv_name || t('results.labels.unknownCandidate'))}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedResult.cv_name || selectedResult.cv_filename || t('results.labels.deletedCv')}
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className={`text-4xl font-bold ${getScoreTextClass(selectedResult.score)}`}>
                  {selectedResult.score}/100
                </div>
                <p className="text-gray-600 mt-1">{t('results.detailModal.globalScore')}</p>
              </div>
              <div className="text-center">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRecommendationColor(selectedResult.recommendation_key)}`}>
                  {getRecommendationLabel(selectedResult.recommendation_key, selectedResult.recommendation)}
                </div>
                <p className="text-gray-600 mt-1">{t('results.detailModal.recommendation')}</p>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600 flex items-center justify-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {t('results.detailModal.analyzedOn').replace('{{date}}', formatResultDate(selectedResult.created_at))}
                </div>
                <p className="text-gray-600 mt-1">{t('results.detailModal.analysisDate')}</p>
              </div>
            </div>

            <div className="space-y-6">
              {detailLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('common.loading') || 'Loading...'}
                </div>
              )}

              <div>
                <h4 className="font-medium text-gray-900 mb-3">{t('results.detailModal.detailedAnalysis')}</h4>
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedResult.analysis}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <ThumbsUp className="w-4 h-4 mr-2 text-green-600" />
                    {t('results.detailModal.strengths')}
                  </h4>
                  <div className="bg-green-50 p-4 rounded-md">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedResult.strengths}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <ThumbsDown className="w-4 h-4 mr-2 text-red-600" />
                    {t('results.detailModal.weaknesses')}
                  </h4>
                  <div className="bg-red-50 p-4 rounded-md">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedResult.weaknesses}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <Target className="w-4 h-4 mr-2 text-blue-600" />
                  {t('results.detailModal.skillsEvaluation')}
                </h4>
                <SkillsRadarChart data={selectedResult.skills_radar || selectedResult.skillsRadar || []} title={t('results.detailModal.skillsRadarTitle')} />
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                {t('results.detailModal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default AnalyzerPage;
